import React, { useCallback, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Plus, Edit, Trash2, X, Save, Play, Clock, FolderOpen, 
  ChevronDown, ChevronUp, ExternalLink, Video
} from 'lucide-react';

import API_URL from '@/config/api';
import VideoCategoryModal from '@/components/video-manager/VideoCategoryModal';
import VideoFormModal from '@/components/video-manager/VideoFormModal';
import VideoMoveModal from '@/components/video-manager/VideoMoveModal';
import { formatApiError } from '@/utils/apiError';
import logger from '@/utils/logger';

const ALL_SECTIONS = [
  { id: 'probound', name: 'ProBound', color: '#2F5E96' },
  { id: 'proepoxy', name: 'ProEpoxy', color: '#7A0B7A' },
  { id: 'prodecor', name: 'ProDecor', color: '#D5AB09' },
  { id: 'protraining', name: 'ProTraining', color: '#D35400' },
];

const SECTION_COLORS = {
  probound: { primary: '#2F5E96', secondary: '#1e3a5f' },
  proepoxy: { primary: '#7A0B7A', secondary: '#5a085a' },
  prodecor: { primary: '#D5AB09', secondary: '#a68507' },
  protraining: { primary: '#D35400', secondary: '#a04000' },
};

const VideoManager = ({ section = 'probound' }) => {
  const [categories, setCategories] = useState([]);
  const [allCategoriesBySection, setAllCategoriesBySection] = useState({});
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Category modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  
  // Video modal
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    category_id: '',
    sections: [section],  // Support multiple sections
    video_url: '',
    thumbnail_url: '',
    duration: '',
    // Direct-upload payload (see VideoFormModal). Null when the video is a
    // pasted URL (YouTube). Populated by the modal's file-upload flow.
    storage_path: null,
    thumbnail_storage_path: null,
    content_type: null,
    size_bytes: null,
  });
  
  // Move modal
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [movingVideo, setMovingVideo] = useState(null);
  const [moveSections, setMoveSections] = useState([]);
  const [moveCategoryId, setMoveCategoryId] = useState('');

  const allSections = ALL_SECTIONS;

  const colors = SECTION_COLORS[section] || SECTION_COLORS.probound;

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/videos/categories?section=${section}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      logger.error('Error fetching categories:', error);
    }
  }, [section]);

  const fetchAllCategories = useCallback(async () => {
    try {
      const results = {};
      for (const s of ALL_SECTIONS) {
        const response = await fetch(`${API_URL}/api/videos/categories?section=${s.id}`, {
          credentials: 'include'
        });
        if (response.ok) {
          results[s.id] = await response.json();
        }
      }
      setAllCategoriesBySection(results);
    } catch (error) {
      logger.error('Error fetching all categories:', error);
    }
  }, []);

  const fetchVideos = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/videos?section=${section}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVideos(data);
      }
    } catch (error) {
      logger.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  }, [section]);

  useEffect(() => {
    fetchCategories();
    fetchAllCategories();
    fetchVideos();
  }, [section, fetchCategories, fetchAllCategories, fetchVideos]);

  // Category handlers
  const handleSaveCategory = async () => {
    if (!categoryName.trim()) return;
    
    try {
      if (editingCategory) {
        await fetch(`${API_URL}/api/videos/categories/${editingCategory.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: categoryName })
        });
      } else {
        await fetch(`${API_URL}/api/videos/categories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: categoryName, section })
        });
      }
      fetchCategories();
      fetchAllCategories();
      setShowCategoryModal(false);
      setCategoryName('');
      setEditingCategory(null);
    } catch (error) {
      logger.error('Error saving category:', error);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/videos/categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        fetchCategories();
        fetchAllCategories();
      } else {
        const error = await response.json();
        alert(formatApiError(error) || 'Error deleting category');
      }
    } catch (error) {
      logger.error('Error deleting category:', error);
    }
  };

  // Reorder a category by swapping it with its neighbour. `direction` is
  // -1 to move up (earlier in the list) or +1 to move down. Persists the
  // new order to the backend so it survives a page refresh.
  const reorderCategory = async (categoryId, direction) => {
    const idx = categories.findIndex((c) => c.id === categoryId);
    if (idx < 0) return;
    const target = idx + direction;
    if (target < 0 || target >= categories.length) return;
    const next = [...categories];
    [next[idx], next[target]] = [next[target], next[idx]];
    // Optimistic local update so the chip visibly moves before the round-trip.
    setCategories(next);
    try {
      await fetch(`${API_URL}/api/videos/categories/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ section, category_ids: next.map((c) => c.id) }),
      });
      // Refresh to pick up any server-side reconciliation.
      fetchCategories();
      fetchAllCategories();
    } catch (error) {
      logger.error('Error reordering categories:', error);
      // Rollback on failure.
      setCategories(categories);
    }
  };

  // Video handlers
  const handleSaveVideo = async () => {
    if (!videoForm.title.trim() || !videoForm.category_id) {
      alert('Please fill in title and select a category');
      return;
    }
    if (!videoForm.sections || videoForm.sections.length === 0) {
      alert('Please select at least one section');
      return;
    }
    // Dual-source guard — must have EITHER a URL or an uploaded storage_path.
    if (!videoForm.video_url?.trim() && !videoForm.storage_path) {
      alert('Add a YouTube link or upload a video file first');
      return;
    }

    try {
      if (editingVideo) {
        await fetch(`${API_URL}/api/videos/${editingVideo.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(videoForm)
        });
      } else {
        await fetch(`${API_URL}/api/videos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(videoForm)
        });
      }
      fetchVideos();
      setShowVideoModal(false);
      resetVideoForm();
    } catch (error) {
      logger.error('Error saving video:', error);
    }
  };

  const toggleSection = (sectionId) => {
    const currentSections = videoForm.sections || [];
    if (currentSections.includes(sectionId)) {
      // Don't allow removing the last section
      if (currentSections.length > 1) {
        setVideoForm({
          ...videoForm,
          sections: currentSections.filter(s => s !== sectionId),
          category_id: '' // Reset category when sections change
        });
      }
    } else {
      setVideoForm({
        ...videoForm,
        sections: [...currentSections, sectionId],
        category_id: '' // Reset category when sections change
      });
    }
  };
  
  // Get all categories from selected sections
  const getAvailableCategories = () => {
    const selectedSections = videoForm.sections || [section];
    const cats = [];
    for (const s of selectedSections) {
      const sectionCats = allCategoriesBySection[s] || [];
      const sectionInfo = allSections.find(sec => sec.id === s);
      for (const cat of sectionCats) {
        cats.push({
          ...cat,
          sectionId: s,
          sectionName: sectionInfo?.name || s,
          sectionColor: sectionInfo?.color || '#666'
        });
      }
    }
    return cats;
  };
  
  // Move video handlers
  const openMoveModal = (video) => {
    setMovingVideo(video);
    setMoveSections(video.sections || [video.section || section]);
    // Pre-select the video's current category so staff can leave it as-is
    // to just change sections, or switch it to move within a section.
    setMoveCategoryId(video.category_id || '');
    setShowMoveModal(true);
  };

  const toggleMoveSection = (sectionId) => {
    if (moveSections.includes(sectionId)) {
      if (moveSections.length > 1) {
        setMoveSections(moveSections.filter(s => s !== sectionId));
      }
    } else {
      setMoveSections([...moveSections, sectionId]);
    }
  };

  const handleMoveVideo = async () => {
    if (!movingVideo || moveSections.length === 0) return;

    try {
      const payload = { sections: moveSections };
      // Only include category_id when it's actually a category that
      // belongs to one of the currently-selected sections. Otherwise
      // leave the video's current category alone.
      if (moveCategoryId) {
        const targetCat = categories.find((c) => c.id === moveCategoryId);
        if (targetCat && moveSections.includes(targetCat.section)) {
          payload.category_id = moveCategoryId;
        }
      }
      await fetch(`${API_URL}/api/videos/${movingVideo.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      fetchVideos();
      setShowMoveModal(false);
      setMovingVideo(null);
      setMoveCategoryId('');
    } catch (error) {
      logger.error('Error moving video:', error);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await fetch(`${API_URL}/api/videos/${videoId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchVideos();
    } catch (error) {
      logger.error('Error deleting video:', error);
    }
  };

  const resetVideoForm = () => {
    setVideoForm({
      title: '',
      description: '',
      category_id: '',
      sections: [section],
      video_url: '',
      thumbnail_url: '',
      duration: '',
      storage_path: null,
      thumbnail_storage_path: null,
      content_type: null,
      size_bytes: null,
    });
    setEditingVideo(null);
  };

  const openEditVideo = (video) => {
    setVideoForm({
      title: video.title,
      description: video.description || '',
      category_id: video.category_id,
      sections: video.sections || [video.section || section],  // Support old and new format
      video_url: video.video_url || '',
      thumbnail_url: video.thumbnail_url || '',
      duration: video.duration || '',
      storage_path: video.storage_path || null,
      thumbnail_storage_path: video.thumbnail_storage_path || null,
      content_type: video.content_type || null,
      size_bytes: video.size_bytes || null,
    });
    setEditingVideo(video);
    setShowVideoModal(true);
  };

  const getYouTubeThumbnail = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
    }
    return null;
  };

  const filteredVideos = activeCategory === 'all' 
    ? videos 
    : videos.filter(v => v.category_id === activeCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: colors.primary }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Video Library</h2>
          <p className="text-sm text-gray-500">Manage training videos for {section.replace('pro', 'Pro')}</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="add-category-btn"
            onClick={() => {
              setCategoryName('');
              setEditingCategory(null);
              setShowCategoryModal(true);
            }}
            variant="outline"
            className="border-gray-300"
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Add Category
          </Button>
          <Button
            data-testid="add-video-btn"
            onClick={() => {
              resetVideoForm();
              setShowVideoModal(true);
            }}
            style={{ backgroundColor: colors.primary }}
            className="text-white hover:opacity-90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Video
          </Button>
        </div>
      </div>

      {/* Categories Bar */}
      <div className="bg-white rounded-xl p-4 shadow-sm border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-700">Categories</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeCategory === 'all'
                ? 'text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            style={activeCategory === 'all' ? { backgroundColor: colors.primary } : {}}
          >
            All ({videos.length})
          </button>
          {categories.map((cat, catIdx) => (
            <div key={cat.id} className="flex items-center gap-1">
              <button
                onClick={() => setActiveCategory(cat.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat.id
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={activeCategory === cat.id ? { backgroundColor: colors.primary } : {}}
              >
                {cat.name} ({videos.filter(v => v.category_id === cat.id).length})
              </button>
              {/* Reorder — up/down. Disabled at either end of the list so
                  staff can't create ambiguous states with 0-position moves. */}
              <button
                type="button"
                onClick={() => reorderCategory(cat.id, -1)}
                disabled={catIdx === 0}
                className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move earlier"
                data-testid={`cat-up-${cat.id}`}
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => reorderCategory(cat.id, 1)}
                disabled={catIdx === categories.length - 1}
                className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move later"
                data-testid={`cat-down-${cat.id}`}
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => {
                  setCategoryName(cat.name);
                  setEditingCategory(cat);
                  setShowCategoryModal(true);
                }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <Edit className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleDeleteCategory(cat.id)}
                className="p-1 text-gray-400 hover:text-red-500"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Videos Grid */}
      {filteredVideos.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border">
          <Video className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <h3 className="font-semibold text-gray-600 mb-2">No videos yet</h3>
          <p className="text-sm text-gray-500 mb-4">Add your first video to get started</p>
          <Button
            onClick={() => {
              resetVideoForm();
              setShowVideoModal(true);
            }}
            style={{ backgroundColor: colors.primary }}
            className="text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Video
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVideos.map((video) => {
            // Priority: manual thumbnail_url > auto-captured (uploaded videos)
            // > YouTube thumbnail derived from URL > solid placeholder.
            const isUploaded = !!video.storage_path;
            const uploadedThumb = isUploaded && video.thumbnail_storage_path
              ? `${API_URL}/api/videos/${video.id}/thumbnail`
              : null;
            const thumbnail = video.thumbnail_url
              || uploadedThumb
              || getYouTubeThumbnail(video.video_url || '')
              || 'https://via.placeholder.com/400x225?text=Video';
            const playHref = isUploaded
              ? `${API_URL}/api/videos/${video.id}/stream`
              : video.video_url;
            const category = categories.find(c => c.id === video.category_id);

            return (
              <div key={video.id} className="bg-white rounded-xl shadow-sm border overflow-hidden group" data-testid={`admin-video-card-${video.id}`}>
                {/* Thumbnail */}
                <div className="relative">
                  <img
                    src={thumbnail}
                    alt={video.title}
                    className="w-full h-40 object-cover"
                    onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/400x225?text=Video'; }}
                  />
                  <a
                    href={playHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <div className="bg-white rounded-full p-3">
                      <Play className="w-6 h-6" style={{ color: colors.primary }} fill={colors.primary} />
                    </div>
                  </a>
                  {isUploaded && (
                    <span className="absolute top-2 left-2 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" title="Uploaded to Ox Resin — not on YouTube">
                      Uploaded
                    </span>
                  )}
                  {video.duration && (
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-0.5 rounded text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {video.duration}
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {category && (
                        <span 
                          className="text-xs font-medium uppercase tracking-wide"
                          style={{ color: colors.primary }}
                        >
                          {category.name}
                        </span>
                      )}
                      <h4 className="font-semibold text-gray-800 mt-1 truncate">{video.title}</h4>
                      {video.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{video.description}</p>
                      )}
                      {/* Show sections badges */}
                      {(video.sections?.length > 1 || (video.sections?.length === 1 && video.sections[0] !== section)) && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(video.sections || [video.section]).map(s => (
                            <span 
                              key={s}
                              className="text-xs px-1.5 py-0.5 rounded text-white"
                              style={{ backgroundColor: allSections.find(sec => sec.id === s)?.color || '#666' }}
                            >
                              {allSections.find(sec => sec.id === s)?.name || s}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button
                        data-testid={`move-video-${video.id}`}
                        onClick={() => openMoveModal(video)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Move to sections"
                      >
                        <FolderOpen className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`edit-video-${video.id}`}
                        onClick={() => openEditVideo(video)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="Edit video"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-video-${video.id}`}
                        onClick={() => handleDeleteVideo(video.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                        title="Delete video"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <VideoCategoryModal
        open={showCategoryModal}
        editing={!!editingCategory}
        categoryName={categoryName}
        setCategoryName={setCategoryName}
        colors={colors}
        onClose={() => setShowCategoryModal(false)}
        onSave={handleSaveCategory}
      />

      <VideoFormModal
        open={showVideoModal}
        editing={!!editingVideo}
        videoForm={videoForm}
        setVideoForm={setVideoForm}
        allSections={allSections}
        toggleSection={toggleSection}
        getAvailableCategories={getAvailableCategories}
        colors={colors}
        onClose={() => setShowVideoModal(false)}
        onSave={handleSaveVideo}
      />

      <VideoMoveModal
        open={showMoveModal}
        movingVideo={movingVideo}
        allSections={allSections}
        moveSections={moveSections}
        toggleMoveSection={toggleMoveSection}
        categories={categories}
        moveCategoryId={moveCategoryId}
        setMoveCategoryId={setMoveCategoryId}
        colors={colors}
        onClose={() => setShowMoveModal(false)}
        onSave={handleMoveVideo}
      />
    </div>
  );
};

export default VideoManager;
